import type { AdminEstimateDneCheck, AdminEstimateDneFields } from '../../models/vendor-bills.model';

/** The banner's view of one estimate (see JobEstimatesSectionComponent.estDneInfoFor / the chat pop-up). */
export interface DneInfo {
  message: string;
  /** Approve is off. */
  hardBlocked: boolean;
  requiredTotal: number | null;
  customerDne: number | null;
  maxVendorAmount: number | null;
  /** The minimum profit the customer's minimum markup asks for (X - vendor amount); null when unknown. */
  minimumProfit?: number | null;
  /** The minimum markup % behind it; null when unknown. */
  markupPercent?: number | null;
}

const DEFAULT_MESSAGE = "This estimate exceeds the customer's DNE.";

function rec(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function pick(o: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(o, k) && o[k] != null) return o[k];
  }
  return undefined;
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function text(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

/**
 * Reads the customer-DNE alert fields off an estimate card / option section (camelCase or PascalCase; null,
 * missing or garbage values become null / absent). Never throws.
 */
export function parseEstimateDneFields(raw: unknown): AdminEstimateDneFields {
  const o = rec(raw);
  if (!o) return {};
  const hard = pick(o, 'approveHardBlocked', 'ApproveHardBlocked');
  return {
    approveDneMessage: text(pick(o, 'approveDneMessage', 'ApproveDneMessage')),
    approveHardBlocked: hard == null ? null : !!hard,
    approveDneRequiredTotal: num(pick(o, 'approveDneRequiredTotal', 'ApproveDneRequiredTotal')),
    approveDneCustomerDne: num(pick(o, 'approveDneCustomerDne', 'ApproveDneCustomerDne')),
    approveDneMaxVendorAmount: num(pick(o, 'approveDneMaxVendorAmount', 'ApproveDneMaxVendorAmount')),
    approveDneMinimumProfit: num(pick(o, 'approveDneMinimumProfit', 'ApproveDneMinimumProfit')),
    approveDneMarkupPercent: num(pick(o, 'approveDneMarkupPercent', 'ApproveDneMarkupPercent')),
  };
}

/**
 * Normalizes the DNE fields on every card and option section of a job-estimates page in place of trusting the
 * wire casing. Other fields are left exactly as received.
 */
export function withParsedEstimateDne<T extends { estimates?: { data?: unknown[] | null } | null }>(page: T): T {
  const data = page?.estimates?.data;
  if (!Array.isArray(data)) return page;
  const fix = (item: unknown): unknown => {
    const o = rec(item);
    if (!o) return item;
    const out: Record<string, unknown> = { ...o, ...parseEstimateDneFields(o) };
    const sections = o['optionSections'] ?? o['OptionSections'];
    if (Array.isArray(sections)) out['optionSections'] = sections.map(fix);
    return out;
  };
  return { ...page, estimates: { ...page.estimates, data: data.map(fix) } } as T;
}

/** Reads the dne-check response data (camelCase or PascalCase). Returns null when it is not an object. */
export function parseEstimateDneCheck(raw: unknown): AdminEstimateDneCheck | null {
  const o = rec(raw);
  if (!o) return null;
  const bool = (...k: string[]): boolean => !!pick(o, ...k);
  return {
    estimateKey: text(pick(o, 'estimateKey', 'EstimateKey')) ?? '',
    blocked: bool('blocked', 'Blocked'),
    message: text(pick(o, 'message', 'Message')),
    requiredTotal: num(pick(o, 'requiredTotal', 'RequiredTotal')),
    customerDne: num(pick(o, 'customerDne', 'CustomerDne')),
    maxVendorAmount: num(pick(o, 'maxVendorAmount', 'MaxVendorAmount')),
    markupPercent: num(pick(o, 'markupPercent', 'MarkupPercent')),
    vendorTotal: num(pick(o, 'vendorTotal', 'VendorTotal')),
    estimateTotal: num(pick(o, 'estimateTotal', 'EstimateTotal')),
    otherVendorsTotal: num(pick(o, 'otherVendorsTotal', 'OtherVendorsTotal')),
    otherVendorCount: num(pick(o, 'otherVendorCount', 'OtherVendorCount')),
    keepAllVendors: bool('keepAllVendors', 'KeepAllVendors'),
    revisedDneMissing: bool('revisedDneMissing', 'RevisedDneMissing'),
  };
}

/**
 * A dne-check answer as a banner view: null when approving is fine (not blocked). Used for the Keep one / Keep all
 * re-check.
 */
export function dneInfoFromCheck(check: AdminEstimateDneCheck | null | undefined): DneInfo | null {
  if (!check || !check.blocked) return null;
  const message = (check.message ?? '').trim() || DEFAULT_MESSAGE;
  return {
    message,
    hardBlocked: true,
    requiredTotal: check.requiredTotal,
    customerDne: check.customerDne,
    maxVendorAmount: check.maxVendorAmount,
    minimumProfit:
      check.requiredTotal != null && check.vendorTotal != null
        ? Math.round((check.requiredTotal - check.vendorTotal) * 100) / 100
        : null,
    markupPercent: check.markupPercent,
  };
}

/** The banner view from the estimate's own fields (card / option section / thread estimate), or null. */
export function dneInfoFromFields(f: AdminEstimateDneFields | null | undefined): DneInfo | null {
  const words = (f?.approveDneMessage ?? '').trim();
  // Blocked with no words (should not happen): still blocked, with the plain sentence.
  const message = words || (f?.approveHardBlocked === true ? DEFAULT_MESSAGE : '');
  if (!message) return null;
  return {
    message,
    hardBlocked: f?.approveHardBlocked !== false,
    requiredTotal: f?.approveDneRequiredTotal ?? null,
    customerDne: f?.approveDneCustomerDne ?? null,
    maxVendorAmount: f?.approveDneMaxVendorAmount ?? null,
    minimumProfit: f?.approveDneMinimumProfit ?? null,
    markupPercent: f?.approveDneMarkupPercent ?? null,
  };
}
